import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth, API } from '../App';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Check, CheckCircle, Sparkles, Zap, Crown, Star, ArrowRight, Clock, Users, Gift, Bell, Loader2, Lock } from 'lucide-react';
import BetaCountdown from '../components/BetaCountdown';
import BetaSpotsCounter from '../components/BetaSpotsCounter';
import { useTranslation } from 'react-i18next';

const Pricing = () => {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [billingCycle, setBillingCycle] = useState('annual');
  const [loading, setLoading] = useState({});
  const [betaStatus, setBetaStatus] = useState(null);
  const [betaLoading, setBetaLoading] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistName, setWaitlistName] = useState('');
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistPosition, setWaitlistPosition] = useState(null);
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const [isFoundingVisionary, setIsFoundingVisionary] = useState(false);
  const [foundingDiscountPercent, setFoundingDiscountPercent] = useState(0);
  
  // Get upgrade source from URL params (first_action, content_generation, dashboard, email, direct)
  const upgradeSource = searchParams.get('source') || 'direct';
  const betaEnded = searchParams.get('beta_ended') === 'true';

  useEffect(() => {
    fetchPlans();
    fetchBetaStatus();
  }, []);

  const fetchPlans = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/subscriptions/plans`, { headers });
      setPlans(response.data.plans);
      setIsFoundingVisionary(response.data.is_founding_visionary || false);
      setFoundingDiscountPercent(response.data.founding_discount_percent || 0);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchBetaStatus = async () => {
    try {
      const response = await axios.get(`${API}/beta/status`);
      setBetaStatus(response.data);
    } catch (error) {
      console.error('Beta status error:', error);
    }
  };

  const handleJoinWaitlist = async () => {
    if (!waitlistEmail) {
      toast.error('Please enter your email');
      return;
    }
    
    setWaitlistLoading(true);
    try {
      const response = await axios.post(`${API}/beta/join-waitlist`, {
        email: waitlistEmail,
        name: waitlistName
      });
      setWaitlistPosition(response.data.position);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join waitlist');
    } finally {
      setWaitlistLoading(false);
    }
  };

  const handleBetaCheckout = async () => {
    if (!user) {
      toast.info('Please sign in to join the Beta program');
      navigate('/auth');
      return;
    }

    setBetaLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/beta/checkout`, {
        origin_url: window.location.origin
      }, { headers });

      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setBetaLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    if (!user) {
      toast.info('Please sign in to subscribe');
      navigate('/auth');
      return;
    }

    if (!cgvAccepted) {
      toast.error('Veuillez accepter les Conditions Générales de Vente');
      return;
    }

    setLoading({ ...loading, [planId]: true });
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/subscriptions/checkout`, {
        plan_id: planId,
        billing_cycle: billingCycle,
        origin_url: window.location.origin,
        upgrade_source: upgradeSource
      }, { headers });

      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setLoading({ ...loading, [planId]: false });
    }
  };

  const getPlanIcon = (planId) => {
    if (planId === 'essentiel') return Sparkles;
    return Crown;
  };

  const getPlanPrice = (plan) => {
    return billingCycle === 'annual' ? plan.price_annual : plan.price_monthly;
  };

  const currentTier = user?.subscription_tier || 'free';

  return (
    <div className="min-h-screen bg-[#050505] noise-overlay">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-8 lg:px-16 py-5 flex items-center justify-between">
          <Link to="/" className="group" data-testid="logo">
            <span className="font-serif text-2xl text-white tracking-wide">Clierxter</span>
            <span className="block text-[#D4AF37] text-[10px] font-accent tracking-[0.3em] mt-0.5">VISION LAB</span>
          </Link>
          
          <div className="flex items-center gap-6">
            {user ? (
              <Button onClick={() => navigate('/dashboard')} className="btn-outline-gold px-6 py-3 text-sm" data-testid="dashboard-btn">
                Dashboard
              </Button>
            ) : (
              <Button onClick={() => navigate('/auth')} className="btn-gold px-6 py-3 text-sm" data-testid="signin-btn">
                Connexion
              </Button>
            )}
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-8 lg:px-16 py-24">
        {/* Beta Ended Banner - Elegant conversion message */}
        {betaEnded && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }} 
            animate={{ opacity: 1, y: 0 }} 
            className="mb-16 p-10 bg-gradient-to-b from-[#0A0A0A] to-[#050505] border border-[#27272A] text-center relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-radial from-[#D4AF37]/5 to-transparent" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-12 bg-gradient-to-b from-[#D4AF37]/50 to-transparent" />
            
            <div className="relative max-w-lg mx-auto">
              {/* Title */}
              <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">
                Tu es arrivée jusqu'ici.
              </h2>
              
              {/* Emotional message */}
              <p className="text-[#A1A1AA] text-lg leading-relaxed mb-4 italic">
                Ce que tu as commencé ne doit pas s'arrêter maintenant.
              </p>
              
              <p className="text-white/80 text-base leading-relaxed mb-2">
                Vision Lab n'était pas une expérience…
              </p>
              <p className="text-[#D4AF37] text-lg font-serif mb-8">
                c'était un point de départ.
              </p>
              
              {/* Closing */}
              <p className="text-[#71717A] text-sm mb-4">
                Si tu choisis de continuer,<br />
                tu continues à construire avec intention.
              </p>
              
              <p className="text-white text-lg font-serif tracking-wide">
                Choisis ton prochain niveau.
              </p>
              
              <p className="text-[#71717A] text-sm mt-6 italic">
                Ce que tu construis ici mérite de continuer.
              </p>
              
              {/* Decorative line */}
              <div className="w-16 h-px bg-[#D4AF37]/30 mx-auto mt-8" />
            </div>
          </motion.div>
        )}

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-20">
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-12 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
            <span className="font-accent text-[#D4AF37] text-[11px] tracking-[0.25em]">TARIFS</span>
            <div className="w-12 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
          </div>
          
          <h1 className="font-serif text-5xl md:text-6xl text-white mb-6">
            Choisis ton
            <span className="text-gold-gradient block mt-2">Parcours Vision</span>
          </h1>
          <p className="text-[#71717A] text-lg max-w-2xl mx-auto font-light mb-12">
            Sélectionne le plan qui correspond à tes objectifs de transformation.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-1 p-1.5 bg-[#0A0A0A] border border-[#1C1C1C]">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-8 py-3 text-sm transition-all ${billingCycle === 'monthly' ? 'bg-[#1C1C1C] text-white' : 'text-[#52525B] hover:text-white'}`}
              data-testid="monthly-toggle"
            >
              Mensuel
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-8 py-3 text-sm transition-all relative ${billingCycle === 'annual' ? 'bg-[#1C1C1C] text-white' : 'text-[#52525B] hover:text-white'}`}
              data-testid="annual-toggle"
            >
              Annuel
              <span className="absolute -top-2 -right-2 text-[8px] font-accent text-[#050505] bg-[#D4AF37] px-2 py-0.5">-17%</span>
            </button>
          </div>
          
          {/* Beta Spots Counter */}
          <div className="mt-10 flex justify-center">
            <BetaSpotsCounter variant="default" showBenefits={false} />
          </div>
        </motion.div>

        {/* Waitlist Banner - When Beta is Full */}
        {/* Waitlist section - only shown when beta is full */}
        {betaStatus && betaStatus.is_full && !waitlistPosition && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-16 relative overflow-hidden"
            data-testid="waitlist-section"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-[#52525B]/10 via-[#71717A]/5 to-[#52525B]/10" />
            <div className="relative border border-[#52525B]/30 p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <Bell className="text-[#D4AF37]" size={24} />
                    <span className="font-accent text-[#D4AF37] text-xs tracking-[0.2em]">BETA COMPLET</span>
                  </div>
                  
                  <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
                    Rejoindre la Liste d'Attente
                  </h2>
                  
                  <p className="text-[#A1A1AA] mb-6 leading-relaxed">
                    Les {betaStatus.total_spots} places Beta sont toutes prises ! Inscrivez-vous sur la liste d'attente 
                    pour être notifié dès qu'une place se libère.
                  </p>
                </div>
                
                <div className="bg-[#0A0A0A] border border-[#1C1C1C] p-8">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-[#71717A] text-sm mb-2">Nom</label>
                      <Input
                        type="text"
                        value={waitlistName}
                        onChange={(e) => setWaitlistName(e.target.value)}
                        placeholder="Votre nom"
                        className="bg-[#0A0A0A] border-[#27272A] text-white"
                        data-testid="waitlist-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-[#71717A] text-sm mb-2">Email</label>
                      <Input
                        type="email"
                        value={waitlistEmail}
                        onChange={(e) => setWaitlistEmail(e.target.value)}
                        placeholder="votre@email.com"
                        className="bg-[#0A0A0A] border-[#27272A] text-white"
                        data-testid="waitlist-email-input"
                      />
                    </div>
                    <Button
                      onClick={handleJoinWaitlist}
                      disabled={waitlistLoading}
                      className="btn-gold w-full py-4"
                      data-testid="join-waitlist-btn"
                    >
                      {waitlistLoading ? (
                        <>
                          <Loader2 className="animate-spin mr-2" size={16} />
                          En cours...
                        </>
                      ) : (
                        <>
                          <Bell className="mr-2" size={16} />
                          Rejoindre la Liste d'Attente
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Waitlist Confirmation */}
        {waitlistPosition && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-16 bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-8 text-center"
            data-testid="waitlist-confirmation"
          >
            <CheckCircle className="text-[#D4AF37] mx-auto mb-4" size={48} />
            <h3 className="font-serif text-2xl text-white mb-2">Vous êtes sur la liste !</h3>
            <p className="text-[#A1A1AA] mb-4">
              Votre position : <span className="text-[#D4AF37] font-bold">#{waitlistPosition}</span>
            </p>
            <p className="text-[#71717A] text-sm">
              Nous vous contacterons dès qu'une place se libère.
            </p>
          </motion.div>
        )}

        {/* Beta Access Banner */}
        {betaStatus && betaStatus.available && betaStatus.phase !== 'ended' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-16 relative overflow-hidden"
          >
            {/* Countdown Component */}
            {betaStatus.countdown && (
              <BetaCountdown 
                countdown={betaStatus.countdown}
                phase={betaStatus.phase}
                betaStartDate={betaStatus.beta_start_date}
                betaEndDate={betaStatus.beta_end_date}
              />
            )}

            <div className="absolute inset-0 bg-gradient-to-r from-[#D4AF37]/10 via-[#E5C585]/5 to-[#D4AF37]/10" />
            <div className="relative border border-[#D4AF37]/30 p-8 md:p-12">
              {/* Beta Badge */}
              <div className="absolute top-0 right-0 bg-[#D4AF37] text-[#050505] px-4 py-1 text-xs font-accent tracking-wider">
                {betaStatus.phase === 'registration' ? 'DISPONIBLE LE 1ER AVRIL' : 'PLACES LIMITÉES'}
              </div>
              
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <Gift className="text-[#D4AF37]" size={24} />
                    <span className="font-accent text-[#D4AF37] text-xs tracking-[0.2em]">OFFRE EXCLUSIVE</span>
                  </div>
                  
                  <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
                    {betaStatus.name}
                  </h2>
                  
                  <p className="text-[#A1A1AA] mb-6 leading-relaxed">
                    {betaStatus.tagline}. Accès complet à Vision Lab Expérience pendant tout le mois d'avril et contribue à façonner l'avenir de Clierxter.
                  </p>
                  
                  <div className="flex flex-wrap gap-4 mb-6">
                    <div className="flex items-center gap-2 text-sm text-[#71717A]">
                      <Clock size={16} className="text-[#D4AF37]" />
                      <span>1 - 30 Avril 2026</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#71717A]">
                      <Users size={16} className="text-[#D4AF37]" />
                      <span>{betaStatus.remaining_spots} places restantes</span>
                    </div>
                  </div>
                  
                  {/* Access locked notice */}
                  {betaStatus.access_locked && (
                    <div className="flex items-center gap-2 text-sm text-[#E5C585] bg-[#D4AF37]/10 px-4 py-2 mb-6">
                      <Lock size={14} />
                      <span>L'accès à la plateforme commence le 1er avril. Inscris-toi maintenant pour réserver ta place !</span>
                    </div>
                  )}
                  
                  <ul className="grid grid-cols-2 gap-2 mb-8">
                    {betaStatus.features?.slice(0, 4).map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                        <Check size={14} className="text-[#D4AF37]" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="text-center md:text-right">
                  <div className="inline-block bg-[#0A0A0A] border border-[#1C1C1C] p-8">
                    <div className="mb-6">
                      <span className="text-[#52525B] text-sm line-through">CHF 69/mois</span>
                      <div className="flex items-baseline justify-center md:justify-end gap-1 mt-2">
                        <span className="text-5xl font-serif text-white">CHF {betaStatus.price}</span>
                        <span className="text-[#71717A] text-sm">unique</span>
                      </div>
                      <p className="text-[#D4AF37] text-sm mt-2">Accès complet Avril (1-30)</p>
                    </div>
                    
                    <Button
                      onClick={handleBetaCheckout}
                      disabled={betaLoading}
                      className="btn-gold w-full py-4"
                      data-testid="beta-checkout-btn"
                    >
                      {betaLoading ? 'Traitement...' : (betaStatus.phase === 'registration' ? 'Réserver ma place' : 'Rejoindre la Beta')}
                      <ArrowRight className="ml-2" size={16} />
                    </Button>
                    
                    <p className="text-[#52525B] text-xs mt-4">
                      Limité aux {betaStatus.total_spots} premières testeuses
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Founding Visionary Banner */}
        {isFoundingVisionary && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10 bg-gradient-to-r from-[#D4AF37]/20 via-[#E5C585]/10 to-[#D4AF37]/20 border border-[#D4AF37]/40 p-6 max-w-3xl mx-auto"
          >
            <div className="flex items-center justify-center gap-3">
              <Sparkles className="text-[#D4AF37]" size={20} />
              <span className="text-[#D4AF37] font-accent tracking-wider text-sm">
                ✨ FOUNDING VISIONARY • {foundingDiscountPercent}% DE RÉDUCTION À VIE
              </span>
              <Sparkles className="text-[#D4AF37]" size={20} />
            </div>
            <p className="text-center text-[#A1A1AA] text-sm mt-2">
              Merci d'avoir cru en Vision Lab depuis le début. Votre réduction s'applique automatiquement.
            </p>
          </motion.div>
        )}

        {/* Plans */}
        <div className="grid md:grid-cols-2 gap-8 items-start max-w-4xl mx-auto">
          {plans.map((plan, index) => {
            const Icon = getPlanIcon(plan.plan_id);
            const price = getPlanPrice(plan);
            const isCurrentPlan = currentTier === plan.plan_id;
            const isExperience = plan.plan_id === 'experience';
            
            // Founding Visionary pricing
            const foundingPrice = plan.has_founding_discount 
              ? (billingCycle === 'annual' ? plan.founding_visionary_price_annual : plan.founding_visionary_price_monthly)
              : null;
            const originalPrice = billingCycle === 'annual' ? plan.price_annual : plan.price_monthly;
            
            return (
              <motion.div
                key={plan.plan_id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.1 }}
                className={`relative ${isExperience ? 'md:-mt-4 md:mb-4' : ''}`}
              >
                {isExperience && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                    <div className="px-6 py-2 bg-gradient-to-r from-[#E5C585] via-[#D4AF37] to-[#A57A03] text-[#050505] text-[10px] font-accent tracking-[0.2em]">
                      RECOMMANDÉ
                    </div>
                  </div>
                )}
                
                <div className={`card-luxury p-10 h-full ${isExperience ? 'border-[#D4AF37]/30 bg-gradient-to-b from-[#0F0F0F] to-[#050505]' : ''}`}>
                  <div className="mb-8">
                    <div className="w-14 h-14 flex items-center justify-center border border-[#27272A] mb-6">
                      <Icon className="text-[#E5C585]" size={26} strokeWidth={1.5} />
                    </div>
                    <h3 className="font-serif text-2xl text-white">{plan.name}</h3>
                    {plan.tagline && (
                      <p className="text-[#71717A] text-sm mt-2 font-light">{plan.tagline}</p>
                    )}
                  </div>

                  <div className="mb-10">
                    {foundingPrice ? (
                      // Founding Visionary pricing display
                      <>
                        <div className="flex items-baseline gap-2">
                          <span className="text-[#52525B] line-through text-2xl">CHF {Number(originalPrice).toFixed(2)}</span>
                        </div>
                        <div className="flex items-baseline gap-2 mt-1">
                          <span className="font-serif text-5xl text-[#D4AF37]">CHF {Number(foundingPrice).toFixed(2)}</span>
                          <span className="text-[#52525B]">/{billingCycle === 'annual' ? 'an' : 'mois'}</span>
                        </div>
                        <p className="text-[#D4AF37] text-sm mt-2 flex items-center gap-2">
                          <Sparkles size={12} />
                          Prix Founding Visionary (-{foundingDiscountPercent}%)
                        </p>
                      </>
                    ) : (
                      // Regular pricing display
                      <>
                        <div className="flex items-baseline gap-2">
                          <span className="font-serif text-5xl text-white">CHF {Number(price).toFixed(2)}</span>
                          <span className="text-[#52525B]">/{billingCycle === 'annual' ? 'an' : 'mois'}</span>
                        </div>
                      </>
                    )}
                    {billingCycle === 'annual' && (
                      <p className="text-[#71717A] text-sm mt-2">
                        {foundingPrice 
                          ? `CHF ${Number(plan.founding_visionary_price_monthly).toFixed(2)}/mois facturé annuellement`
                          : `CHF ${Number(plan.price_monthly).toFixed(2)}/mois facturé annuellement`
                        }
                      </p>
                    )}
                  </div>

                  <ul className="space-y-4 mb-10">
                    {plan.features?.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center border border-[#D4AF37]/30 flex-shrink-0 mt-0.5">
                          <Check className="text-[#D4AF37]" size={12} />
                        </div>
                        <span className="text-[#A1A1AA] text-sm font-light">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => handleSubscribe(plan.plan_id)}
                    disabled={loading[plan.plan_id] || isCurrentPlan}
                    className={`w-full py-5 flex items-center justify-center gap-2 ${isExperience ? 'btn-gold' : 'btn-outline-gold'} ${isCurrentPlan ? 'opacity-50 cursor-not-allowed' : ''}`}
                    data-testid={`subscribe-${plan.plan_id}`}
                  >
                    {loading[plan.plan_id] ? (
                      'Traitement...'
                    ) : isCurrentPlan ? (
                      'Plan actuel'
                    ) : (
                      <>Commencer <ArrowRight size={16} /></>
                    )}
                  </Button>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* CGV Checkbox - Required before subscription */}
        {user && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="max-w-xl mx-auto mt-12"
          >
            <label className="flex items-start gap-3 cursor-pointer group">
              <div className="relative mt-0.5">
                <input
                  type="checkbox"
                  checked={cgvAccepted}
                  onChange={(e) => setCgvAccepted(e.target.checked)}
                  className="sr-only peer"
                  data-testid="cgv-checkbox"
                />
                <div className={`w-5 h-5 border transition-all flex items-center justify-center ${
                  cgvAccepted 
                    ? 'border-[#D4AF37] bg-[#D4AF37]/10' 
                    : 'border-[#3F3F46] group-hover:border-[#52525B]'
                }`}>
                  {cgvAccepted && <Check className="text-[#D4AF37]" size={12} />}
                </div>
              </div>
              <span className="text-[#71717A] text-sm leading-relaxed">
                J'ai lu et j'accepte les{' '}
                <Link 
                  to="/cgv" 
                  className="text-[#D4AF37] hover:underline"
                  target="_blank"
                >
                  Conditions Générales de Vente
                </Link>
              </span>
            </label>
          </motion.div>
        )}

        {/* Bottom Info */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ delay: 0.5 }} 
          className="mt-20 text-center"
        >
          <div className="inline-flex items-center gap-3 mb-6">
            <Star className="text-[#D4AF37]" size={16} />
            <span className="text-[#71717A] text-sm font-light">Tous les plans incluent l'accès à la communauté et aux challenges</span>
            <Star className="text-[#D4AF37]" size={16} />
          </div>
          <p className="text-[#52525B] text-sm">
            Les abonnées annuelles reçoivent leur stratégie complète dès la souscription.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Pricing;
